// src/hooks/useStudentProfile.ts
// UPDATED: 2026-03-27
// ✅ Decodifica JWT localmente — cada conta vê só seus dados
// ✅ Filtra pelo userId exato do token
// ✅ Sem chamar /auth/me com POST

import { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';

export interface StudentProfile {
  id: string; systemId: string; fullName: string;
  cpf: string | null; cin: string | null; phone: string | null;
  birthdate: string | null; address: string | null;
  city: string | null; state: string | null; photoUrl: string | null;
  courseName: string | null; courseWorkload: number | null;
  courseStartDate: string | null; courseEndDate: string | null;
  enrollmentDate: string;
  status: 'active' | 'graduated' | 'suspended' | 'inactive';
  affiliateId: string; userId: string;
  certificateCount: number; carteiraGerada: boolean; carteiraId: string | null;
  evasionFlag: boolean;
  diplomaRequests: Array<{
    id: string; type: 'diploma' | 'segunda-via';
    status: 'pending' | 'approved' | 'rejected' | 'sent';
    requestedAt: string; resolvedAt?: string;
  }> | null;
  affiliate?: { id: string; schoolName: string; city?: string; state?: string } | null;
  user?: { id: string; email: string; fullName: string } | null;
}

// Decodifica payload JWT sem verificar assinatura
function decodeJwt(token: string): Record<string, any> | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch { return null; }
}

function getToken(): string | null {
  return localStorage.getItem('auth_token')
    ?? localStorage.getItem('token')
    ?? localStorage.getItem('access_token')
    ?? sessionStorage.getItem('auth_token')
    ?? null;
}

export function useStudentProfile() {
  const [student, setStudent] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const fetch_ = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // 1. Decodifica o token para pegar userId sem fazer requisição
      const token   = getToken();
      const payload = token ? decodeJwt(token) : null;
      const userId  = payload?.sub ?? payload?.userId ?? payload?.id ?? null;
      const sysId   = payload?.systemId ?? payload?.system_id ?? null;

      if (!userId && !sysId) throw new Error('Sessão expirada. Faça login novamente.');

      let data: StudentProfile | null = null;

      // 2. Rota mais direta: busca pelo systemId do token
      if (sysId) {
        try {
          const ref: any = await apiClient.get(`/students/system/${sysId}`);
          if (ref?.id) data = await apiClient.get(`/students/${ref.id}`) as any;
        } catch { /* continua */ }
      }

      // 3. Fallback: lista e filtra localmente pelo userId exato
      if (!data && userId) {
        const res: any = await apiClient.get('/students?limit=200');
        const list: any[] = res?.students ?? [];
        // CRÍTICO: filtra pelo userId do token — garante isolamento por conta
        data = list.find((s: any) => s.userId === userId) ?? null;
      }

      // 4. Último recurso: /students/me (se o backend implementar)
      if (!data) {
        try { data = await apiClient.get('/students/me') as any; } catch { /* não existe */ }
      }

      if (!data) throw new Error('Perfil não encontrado para esta conta.');
      setStudent(data);
    } catch (e: any) {
      setError(e?.message ?? 'Erro ao carregar perfil');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);
  return { student, loading, error, refetch: fetch_ };
}